import { useYouTube, YT } from "@hooks/useYouTube"
import { cn } from "@utils/tw"
import { Ref, useEffect, useMemo, useRef, useState } from "react"

type Props = {
  playerRef?: Ref<YT.Player | null>
  className?: string
  videoId: string
  playsInline?: boolean
  autoplay?: boolean
  muted?: boolean
  controls?: boolean
  loop?: boolean
  onReady?: (player: YT.Player) => void
  onError?: (player: YT.Player, error: YT.OnErrorEvent) => void
  onPlaying?: (player: YT.Player) => void
  onPaused?: (player: YT.Player) => void
  onBuffering?: (player: YT.Player) => void
  onUnstarted?: (player: YT.Player) => void
  onEnded?: (player: YT.Player) => void
  startSeconds?: number
}

export function YouTubeVideo(props: Props) {
  const ref = useRef<HTMLDivElement>(null!)
  const YT = useYouTube()
  const [player, setPlayer] = useState<YT.Player | null>(null)

  const eventProps = useRef(props)
  eventProps.current = props
  useEffect(() => {
    if (YT) {
      const props = eventProps.current
      const container = document.createElement("div")
      ref.current.appendChild(container)
      const player = new YT.Player(container, {
        playerVars: {
          origin: document.location.origin,
          autoplay: props.autoplay,
          autohide: 1,
          controls: props.controls,
          cc_load_policy: 0,
          iv_load_policy: 0,
          disablekb: 1,
          mute: props.muted,
          playsinline: props.playsInline,
          modestbranding: 1,
          showinfo: 0,
          rel: 0,
          ss: 1,
          disable_native_context_menu: 1,
          showwatchlater: "0",
          profile_picture: 0,
          co_rel: 1,
        } as any,
        videoId: getYouTubeID(props.videoId),
        width: "100%",
        height: "100%",
        events: {
          onReady: (e) => {
            setPlayer(e.target)
          },
          onError: (e) => {
            props.onError?.(e.target, e)
          },
          onStateChange: (e) => {
            if (e.data === YT!.PlayerState.ENDED) {
              if (props.loop) {
                player.seekTo(0, true)
              }
              props.onEnded?.(player)
            } else if (e.data === YT!.PlayerState.PLAYING) {
              props.onPlaying?.(player)
            } else if (e.data === YT!.PlayerState.PAUSED) {
              props.onPaused?.(player)
            } else if (e.data === YT!.PlayerState.BUFFERING) {
              props.onBuffering?.(player)
            } else if (e.data === YT!.PlayerState.UNSTARTED) {
              props.onUnstarted?.(player)
            }
          },
        },
      })
      return () => {
        try {
          player?.destroy()
        } catch (er) {}
        container.remove()
      }
    }
  }, [YT, props.playsInline])

  useEffect(() => {
    if (player) {
      const videoId = getYouTubeID(props.videoId)
      if (player.getVideoUrl().includes(videoId)) return
      player.loadVideoById({ videoId: props.videoId, startSeconds: props.startSeconds ?? 0 })
    }
  }, [props.videoId, player])

  useEffect(() => {
    if (player) {
      try {
        if (player.isMuted() !== props.muted) {
          if (props.muted) {
            player.mute()
          } else {
            player.unMute()
          }
        }
      } catch (e) {}
    }
  }, [player, props.muted])

  return <div ref={ref} className={cn("aspect-video", props.className)}></div>
}

export function getYouTubeID(url: string) {
  const [a, , b] = url
    ?.replace(/(>|<)/gi, "")
    .split(
      /^.*(?:(?:youtu\.?be(\.com)?\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/)|(?:(?:watch)?\?v(?:i)?=|\&v(?:i)?=))([^#\&\?]*).*/,
    )
  if (b !== undefined) {
    return b.split(/[^0-9a-z_-]/i)[0]
  } else {
    return a
  }
}
